// Formatadores puros do cockpit do imóvel (ImovelShow).
// Nenhuma função aqui toca store, rota ou API: só transforma dados em texto.
import camelcaseKeys from 'camelcase-keys';
import {
  ENTRY_ACCESS_KIND_OPTIONS,
  IDENTITY_CONFLICT_LABELS,
  IDENTITY_SOURCE_LABELS,
  LISTING_DIFF_FIELD_LABELS,
  LISTING_TAG_LABELS,
  MEDIA_QA_REQUIREMENT_LABELS,
  SECTION_ALIASES,
} from './imovelShowConstants';

export const normalizePayload = payload =>
  camelcaseKeys(payload || {}, { deep: true });

export const normalizeSection = value => {
  const section = value?.toString() || 'resumo';
  return SECTION_ALIASES[section] || section;
};

export const formatBRL = v =>
  v
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(v)
    : null;

export const positiveNumber = value => {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const compactDifferencePercent = (nextPrice, previousPrice) => {
  if (!previousPrice) return null;

  const percent = ((nextPrice - previousPrice) / previousPrice) * 100;
  return Number(percent.toFixed(2));
};

export const formatDateTime = value => {
  if (!value) return 'Não sincronizado';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Não sincronizado';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatDiffValue = value => {
  if (value === null || value === undefined || value === '') return 'Vazio';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.length ? `${value.length} itens` : 'Sem itens';
  }
  if (typeof value === 'object') return 'Objeto redigido';

  return String(value);
};

export const actionErrorMessage = (error, fallback) =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  fallback;

export const confirmSensitiveAction = message =>
  typeof window === 'undefined' ||
  // eslint-disable-next-line no-alert
  window.confirm(message);

export const tagLabel = tag => LISTING_TAG_LABELS[tag] || tag;

export const listingDiffFieldLabel = field =>
  LISTING_DIFF_FIELD_LABELS[field] || field;

export const normalizeDiffEntries = diff => {
  if (!diff || typeof diff !== 'object') return [];

  return Object.entries(diff).map(([field, change]) => ({
    field,
    label: listingDiffFieldLabel(field),
    from: formatDiffValue(change?.from),
    to: formatDiffValue(change?.to),
  }));
};

export const identitySourceLabel = source =>
  IDENTITY_SOURCE_LABELS[source] || source || 'Origem não informada';

export const identityConfidenceLabel = confidence => {
  const value = Number(confidence);
  if (!Number.isFinite(value)) return 'Confiança não informada';
  return `${Math.round(value * 100)}%`;
};

export const identityConflictLabel = candidate =>
  IDENTITY_CONFLICT_LABELS[candidate?.matchType] ||
  candidate?.matchType ||
  'Conflito de identidade';

export const identityFieldLabels = entry => {
  const fields = Array.isArray(entry?.changedFields) ? entry.changedFields : [];
  return (
    fields
      .map(field => field.label || field.field)
      .filter(Boolean)
      .join(', ') || 'Campos não informados'
  );
};

export const identityHistoryDateLabel = entry =>
  entry?.occurredAt ? formatDateTime(entry.occurredAt) : 'Sem data';

export const entryAccessKindLabel = value =>
  ENTRY_ACCESS_KIND_OPTIONS.find(option => option.value === value)?.label ||
  value ||
  'Acesso';

export const entryAccessSummaryRows = entry => [
  { label: 'Tipo', value: entry.accessLabel || entry.kind },
  { label: 'Responsável', value: entry.assignee?.name },
  { label: 'Telefone', value: entry.assignee?.phone },
  { label: 'E-mail', value: entry.assignee?.email },
  { label: 'Estado da chave', value: entry.keyState },
  {
    label: 'Validação',
    value: entry.keyHolderValidationEnabled ? 'Opt-in ativo' : 'Opt-in inativo',
  },
];

export const entryAccessDetailRows = entry =>
  Array.isArray(entry.details) ? entry.details : [];

export const mediaQaRequirementLabel = requirement =>
  MEDIA_QA_REQUIREMENT_LABELS[requirement.key] ||
  requirement.key ||
  'Requisito';

export const mediaQaStatusTone = status => {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'warning';
  if (!status) return 'warning';
  return 'danger';
};

export const mediaQaStatusLabel = status => {
  if (status === 'approved') return 'Aprovado';
  if (status === 'pending') return 'Pendente';
  if (!status) return 'Pendente';
  return 'Reprovado';
};

export const formatMediaByteSize = value => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return 'Tamanho pendente';
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

export const mediaQaItemTitle = item =>
  item.altText ||
  `Mídia ${item.sortOrder !== undefined ? item.sortOrder + 1 : item.id}`;

export const mediaQaActorLabel = item => {
  const actor = item.qa?.actor?.name;
  const decidedAt = item.qa?.decidedAt ? formatDateTime(item.qa.decidedAt) : '';
  return (
    [actor, decidedAt].filter(Boolean).join(' · ') || 'Sem decisão registrada'
  );
};

export const taxFeeOriginDateLabel = value =>
  value ? formatDateTime(value) : 'Origem sem data';

export const taxFeeHistoryRows = row =>
  Array.isArray(row.history) ? row.history.slice(-3).reverse() : [];

export const formatTaxFeeSimulationTotalValue = (key, value) => {
  if (key === 'termMonths') {
    return `${value} meses`;
  }

  if (key === 'interestRateYear') {
    return `${Number(value) * 100}%`;
  }

  return formatBRL(value) || value;
};

export const taxFeeSimulationHistoryLabel = entry => {
  const result = normalizePayload(entry?.result || {});
  const total =
    result.totals?.acquisitionTaxes ||
    result.totals?.acquisition_taxes ||
    entry?.result?.totals?.acquisition_taxes;
  return [
    formatDateTime(entry?.calculated_at || entry?.calculatedAt),
    total ? `total ${formatBRL(total)}` : null,
    entry?.input?.payment_kind || entry?.input?.paymentKind,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const toDatetimeLocalValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const portalActionErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const retryAfter = error?.response?.data?.retry_after;
  const cause = code ? ` Causa: ${code}.` : '';

  if (status === 401 || status === 403) {
    return `Conexão OLX expirada. Reautorize a integração.${cause}`;
  }

  if (status === 429) {
    return retryAfter
      ? `OLX limitou as requisições. Tente novamente em ${retryAfter} segundos.${cause}`
      : `OLX limitou as requisições. Tente novamente em instantes.${cause}`;
  }

  return `${actionErrorMessage(error, fallback)}${cause}`;
};

export const previewActionName = action =>
  action === 'relist' ? 'reactivate' : action;
